import logError from 'Helpers/logError';
import openModal from 'App/services/Modal';
import { isProductNewsEnabled } from 'App/helpers/productNewsPage';

/**
 * Announce a release this user has not seen, which is only knowable once the app has its
 * data. `onSeen` is called once, when the modal has shown the reader the releases.
 *
 * Comparing ids, not dates: a new release always takes a higher id, while re-publishing
 * an old one keeps its original date.
 */
export const announceProductNews = ({
	publishedIds,
	recentIds,
	lastSeenId,
	onSeen,
	readLastSeenId,
	onConfirmed
}: {
	publishedIds: number[];
	recentIds: number[];
	lastSeenId: number;
	onSeen: (seenId: number) => void;
	/** The marker as the master DB holds it, rather than as the cached /alliwant body had it. */
	readLastSeenId: () => Promise<number>;
	/** Handed the marker from the master DB, so the navbar's unseen count can be corrected too. */
	onConfirmed: (confirmedId: number) => void;
}) => {
	if (!isProductNewsEnabled() || !publishedIds?.length) {
		return;
	}

	// Only an actual new release is announced unprompted. A reader who has never seen
	// the releases has the whole backlog unseen, and pushing that at them as news is wrong.
	const isNews = (seenId: number) => !!recentIds?.some(id => id > seenId);
	if (!isNews(lastSeenId)) {
		return;
	}

	// The marker is the highest published id, not the highest unread one: it means the
	// backlog behind an announcement counts as seen too.
	const highest = Math.max(...publishedIds, lastSeenId);

	// Confirmed against the master DB before announcing (see `readLastSeenReleaseNoteId`), which
	// costs a request only when there is something to announce.
	// Opened through the registry rather than by importing the component: this file is on
	// the boot path, and a static import would pull the modal's chunk into it. It marks
	// them seen itself, once the digest is on screen — marking here would burn the
	// announcement on a chunk that fails to load.
	const announce = () => openModal('ProductNews', { onSeen: () => onSeen(highest) });

	readLastSeenId()
		.then(confirmed => {
			onConfirmed(confirmed);
			if (isNews(Math.max(lastSeenId, confirmed))) {
				announce();
			}
		})
		.catch(e => {
			// Announce anyway: showing a release twice beats never showing it.
			logError(e, 'Product news: could not confirm what this reader has seen');
			announce();
		});
};
